import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconMessagePlus, IconTrash, IconX } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  useCommentsForTarget,
  useDeleteComment,
  useEditComment,
  usePostComment,
} from '../../queries/comments';
import { useAuthStore } from '../../auth/auth-store';
import { useProjectStore } from '../../project/project-store';
import { ApiError } from '../../lib/api-error';
import type { Comment, CommentTarget } from '../../types/api';

interface Props {
  target: { type: CommentTarget; id: string | null; label: string };
  onClearSelection?: () => void;
}

export function CommentsPanel({ target, onClearSelection }: Props) {
  const { data: comments = [], isLoading } = useCommentsForTarget(target);
  const user = useAuthStore((s) => s.user);
  const role = useProjectStore((s) => s.projectRole);
  const isModOrOwner = role === 'owner' || role === 'moderator';

  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const postM = usePostComment();
  const editM = useEditComment();
  const deleteM = useDeleteComment();

  const threaded = useMemo(() => buildThreads(comments), [comments]);

  async function onPost() {
    const body = draft.trim();
    if (!body || !target.id) return;
    try {
      await postM.mutateAsync({
        target_type: target.type,
        target_id: target.id,
        body,
        ...(replyTo ? { parent_comment_id: replyTo } : {}),
      });
      setDraft('');
      setReplyTo(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Post failed',
        message: err instanceof ApiError ? err.message : 'Retry?',
      });
    }
  }

  if (!target.id) {
    return (
      <Paper withBorder p="md" radius="md" h="100%">
        <Text c="dimmed" size="sm">
          Select an entry or the run header to see comments.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius="md" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" p="sm" wrap="nowrap">
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Comments
          </Text>
          <Group gap={6}>
            <Badge variant="light" size="xs" color={targetColor(target.type)}>
              {target.type.replace('_', ' ')}
            </Badge>
            <Text size="sm" lineClamp={1}>
              {target.label}
            </Text>
          </Group>
        </Stack>
        {onClearSelection && target.type === 'runsheet_entry' && (
          <Tooltip label="Show comments on the run instead">
            <ActionIcon variant="subtle" size="sm" onClick={onClearSelection} aria-label="clear">
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      <Divider />

      <ScrollArea style={{ flex: 1 }} p="sm">
        {isLoading && (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        )}
        {!isLoading && threaded.length === 0 && (
          <Text size="sm" c="dimmed">
            No comments yet. Be the first to leave a note.
          </Text>
        )}
        <Stack gap="sm">
          {threaded.map((t) => (
            <CommentNode
              key={t.comment.id}
              node={t}
              currentUserId={user?.id ?? null}
              canModerate={isModOrOwner}
              editingId={editingId}
              editDraft={editDraft}
              onStartEdit={(c) => {
                setEditingId(c.id);
                setEditDraft(c.body);
              }}
              onCancelEdit={() => {
                setEditingId(null);
                setEditDraft('');
              }}
              onSaveEdit={async (c) => {
                try {
                  await editM.mutateAsync({ id: c.id, body: editDraft.trim() });
                  setEditingId(null);
                  setEditDraft('');
                } catch (err) {
                  notifications.show({
                    color: 'red',
                    title: 'Update failed',
                    message: err instanceof ApiError ? err.message : 'Retry?',
                  });
                }
              }}
              onEditDraftChange={setEditDraft}
              onDelete={(c) =>
                modals.openConfirmModal({
                  title: 'Delete comment?',
                  labels: { confirm: 'Delete', cancel: 'Cancel' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => deleteM.mutate(c.id),
                })
              }
              onReply={(c) => setReplyTo(c.id)}
              depth={0}
            />
          ))}
        </Stack>
      </ScrollArea>

      <Divider />
      <Box p="sm">
        {replyTo && (
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">
              Replying to {replyTo.slice(0, 8)}
            </Text>
            <ActionIcon size="xs" variant="subtle" onClick={() => setReplyTo(null)}>
              <IconX size={12} />
            </ActionIcon>
          </Group>
        )}
        <Textarea
          autosize
          minRows={2}
          maxRows={6}
          placeholder="Add a note…"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              void onPost();
            }
          }}
        />
        <Group justify="space-between" mt={6}>
          <Text size="xs" c="dimmed">
            Cmd/Ctrl + Enter to post
          </Text>
          <Button
            size="xs"
            leftSection={<IconMessagePlus size={14} />}
            onClick={onPost}
            loading={postM.isPending}
            disabled={!draft.trim()}
          >
            Post
          </Button>
        </Group>
      </Box>
    </Paper>
  );
}

function targetColor(t: CommentTarget): string {
  switch (t) {
    case 'title_run':
      return 'pine';
    case 'runsheet_entry':
      return 'blue';
    case 'tract':
      return 'orange';
    default:
      return 'gray';
  }
}

interface ThreadNode {
  comment: Comment;
  replies: ThreadNode[];
}

function authorLabel(c: Comment, isMe: boolean): string {
  const name = c.author_username ?? `user ${c.author_user_id.slice(0, 8)}`;
  return isMe ? `${name} (you)` : name;
}

function authorInitials(c: Comment): string {
  const source = c.author_username ?? c.author_user_id;
  return source
    .split(/[._\s-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

function buildThreads(comments: Comment[]): ThreadNode[] {
  const byId = new Map<string, ThreadNode>();
  for (const c of comments) byId.set(c.id, { comment: c, replies: [] });
  const roots: ThreadNode[] = [];
  for (const node of byId.values()) {
    if (node.comment.parent_comment_id) {
      const parent = byId.get(node.comment.parent_comment_id);
      if (parent) parent.replies.push(node);
      else roots.push(node);
    } else {
      roots.push(node);
    }
  }
  const sort = (a: ThreadNode, b: ThreadNode) =>
    a.comment.created_at.localeCompare(b.comment.created_at);
  roots.sort(sort);
  for (const n of byId.values()) n.replies.sort(sort);
  return roots;
}

function CommentNode({
  node,
  currentUserId,
  canModerate,
  editingId,
  editDraft,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditDraftChange,
  onDelete,
  onReply,
  depth,
}: {
  node: ThreadNode;
  currentUserId: string | null;
  canModerate: boolean;
  editingId: string | null;
  editDraft: string;
  onStartEdit: (c: Comment) => void;
  onCancelEdit: () => void;
  onSaveEdit: (c: Comment) => void;
  onEditDraftChange: (v: string) => void;
  onDelete: (c: Comment) => void;
  onReply: (c: Comment) => void;
  depth: number;
}) {
  const c = node.comment;
  const isAuthor = c.author_user_id === currentUserId;
  const canDelete = isAuthor || canModerate;
  const isEditing = editingId === c.id;

  return (
    <Stack gap={4} pl={depth * 14}>
      <Paper
        withBorder={depth === 0}
        p="xs"
        radius="sm"
        {...(depth > 0 ? { bg: 'transparent' } : {})}
      >
        <Group justify="space-between" wrap="nowrap" mb={4}>
          <Group gap={8} wrap="nowrap">
            <Avatar size="sm" radius="xl" color="pine">
              {authorInitials(c)}
            </Avatar>
            <Stack gap={0}>
              <Text size="xs" fw={500}>
                {authorLabel(c, isAuthor)}
              </Text>
              <Text size="xs" c="dimmed">
                {dayjs(c.created_at).format('YYYY-MM-DD HH:mm')}
                {c.updated_at !== c.created_at && ' · edited'}
              </Text>
            </Stack>
          </Group>
          <Group gap={2}>
            <Tooltip label="Reply">
              <ActionIcon size="xs" variant="subtle" onClick={() => onReply(c)}>
                <IconMessagePlus size={12} />
              </ActionIcon>
            </Tooltip>
            {isAuthor && !isEditing && (
              <Tooltip label="Edit">
                <ActionIcon size="xs" variant="subtle" onClick={() => onStartEdit(c)}>
                  <IconEdit size={12} />
                </ActionIcon>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip label="Delete">
                <ActionIcon size="xs" variant="subtle" color="red" onClick={() => onDelete(c)}>
                  <IconTrash size={12} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
        {isEditing ? (
          <Stack gap={4}>
            <Textarea
              autosize
              minRows={2}
              value={editDraft}
              onChange={(e) => onEditDraftChange(e.currentTarget.value)}
            />
            <Group gap="xs">
              <Button size="xs" onClick={() => onSaveEdit(c)}>
                Save
              </Button>
              <Button size="xs" variant="default" onClick={onCancelEdit}>
                Cancel
              </Button>
            </Group>
          </Stack>
        ) : (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {c.body}
          </Text>
        )}
      </Paper>
      {node.replies.map((reply) => (
        <CommentNode
          key={reply.comment.id}
          node={reply}
          currentUserId={currentUserId}
          canModerate={canModerate}
          editingId={editingId}
          editDraft={editDraft}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onEditDraftChange={onEditDraftChange}
          onDelete={onDelete}
          onReply={onReply}
          depth={depth + 1}
        />
      ))}
    </Stack>
  );
}
